import { useCallback, useEffect, useState } from "react";
import { Alert, Link } from "@mui/material";
import { useStateValue } from "../../context/StateValueContext";
import { useApi } from "../../hooks/useApi";
import { PERMISSIONS } from "../../constants/permissions";
import { BillingHealth, BillingProfileHealth } from "../../types/billing";
import { CmLink } from "./CmLink";

/**
 * Persistent warning for client admins whose company is billing-enrolled but
 * has no default payment method set in Stripe. Mounted once in AppShell; hides
 * itself if the condition doesn't apply.
 */
export default function BillingPaymentMethodBanner() {
  const [{ user, billingNeedsPaymentMethod }, dispatch] = useStateValue();
  const { get, post } = useApi();
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [billingProfiles, setBillingProfiles] = useState<BillingProfileHealth[]>([]);

  const hasPermission = !!user?.permissions?.includes(PERMISSIONS.BILLING_OWN);
  const companyId = user?.companyId;

  const refresh = useCallback(async () => {
    if (!hasPermission) return;
    try {
      const health = await get<BillingHealth>(`/api/client-billing/health`);
      setBillingProfiles(health.billingProfiles ?? []);
      dispatch({ type: "SET_BILLING_NEEDS_PAYMENT_METHOD", billingNeedsPaymentMethod: health.needsPaymentMethod });
    } catch (err) {
      console.error("Failed to refresh billing status:", err);
    }
  }, [hasPermission, get, dispatch]);

  // Re-fetch on mount and whenever the active company changes. Clear stale
  // state first so the banner doesn't flash the previous company's status.
  useEffect(() => {
    if (!hasPermission || !companyId) return;
    dispatch({ type: "SET_BILLING_NEEDS_PAYMENT_METHOD", billingNeedsPaymentMethod: null });
    refresh();
  }, [companyId, hasPermission, refresh, dispatch]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const handleOpenPortal = async () => {
    setPortalError(null);
    setOpeningPortal(true);
    try {
      const { url } = await post<{ url: string }>(
        `/api/client-billing/billing-profiles/${billingProfiles[0].billingProfileId}/portal-session`,
        { returnUrl: window.location.href, flow: "payment_method_update" },
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      setPortalError(err.message || "Failed to open billing portal");
    } finally {
      setOpeningPortal(false);
    }
  };

  if (!hasPermission || billingNeedsPaymentMethod !== true) return null;

  if (billingProfiles.length > 1) {
    return (
      <Alert severity="error" sx={{ borderRadius: 0 }}>
        {billingProfiles.length} billing profiles have no default payment method.{" "}
        <Link component={CmLink} to="/billing" sx={{ verticalAlign: "baseline", fontWeight: 600 }}>
          Add one for each
        </Link>
        {" "}to avoid service interruption.
      </Alert>
    );
  }

  return (
    <Alert severity="error" sx={{ borderRadius: 0 }}>
      {portalError ? `${portalError}. ` : "No default payment method on file. Please "}
      <Link
        component="button"
        onClick={handleOpenPortal}
        disabled={openingPortal || billingProfiles.length === 0}
        sx={{ verticalAlign: "baseline", fontWeight: 600 }}
      >
        {openingPortal ? "opening…" : "add one"}
      </Link>
      {" "}to avoid service interruption.
    </Alert>
  );
}
